import { initializeApp } from 'firebase/app';
import { getAuth, connectAuthEmulator, Auth } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
};

// Only log in development
const isDev = import.meta.env.DEV;
const log = (...args: any[]) => isDev && console.log(...args);

log("🔥 Firebase Config:", {
  projectId: firebaseConfig.projectId,
  platform: 'web'
});

// ---------------------------
// 🚀 Initialize Firebase
// ---------------------------
const app = initializeApp(firebaseConfig);
log("✅ Firebase initialized");

// Initialize auth with default browser persistence
const auth: Auth = getAuth(app);
log("🌐 Auth initialized with default browser persistence");

export { auth };
export const db = getFirestore(app);
export const storage = getStorage(app);
export const functions = getFunctions(app);

// ---------------------------
// 🔐 APP CHECK CONFIGURATION
// ---------------------------

// WEB BROWSER: Use reCAPTCHA
if (import.meta.env.VITE_FIREBASE_RECAPTCHA_SITE_KEY) {
  log("🌍 Web browser detected — using reCAPTCHA AppCheck");
  try {
    if (import.meta.env.DEV) {
      // @ts-ignore
      self.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
    }

    initializeAppCheck(app, {
      provider: new ReCaptchaV3Provider(import.meta.env.VITE_FIREBASE_RECAPTCHA_SITE_KEY),
      isTokenAutoRefreshEnabled: true
    });

    log("✅ Web AppCheck initialized");
  } catch (e) {
    console.error("❌ Web AppCheck failed:", e);
  }
} else {
  if (isDev) console.warn("⚠️ App Check not configured for web — missing site key");
}

// ---------------------------
// 🧪 EMULATORS FOR WEB DEV
// ---------------------------
if (import.meta.env.DEV && import.meta.env.VITE_USE_FIREBASE_EMULATORS === "true") {
  console.log("🔧 Connecting to Firebase emulators...");
  connectAuthEmulator(auth, "http://localhost:9099", { disableWarnings: true });
  connectFirestoreEmulator(db, "localhost", 8080);
  connectFunctionsEmulator(functions, "localhost", 5001);
  connectStorageEmulator(storage, "localhost", 9199);
}

export default app;
